import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Send, Paperclip, Mic, MicOff, Search, Loader2, MessageSquare, Image as ImageIcon,
  Video as VideoIcon, FileText as FileIcon, Smile, Phone, ArrowLeft, CheckCheck, Clock, AlertTriangle, X, MapPin, Reply
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function StatusTicks({ status }) {
  if (status === 'failed') return <AlertTriangle className="h-3.5 w-3.5 text-[#9F1239]" />;
  if (status === 'pending' || status === 'sent') return <CheckCheck className="h-3.5 w-3.5 text-muted-foreground/60" />;
  if (status === 'delivered') return <CheckCheck className="h-3.5 w-3.5 text-muted-foreground" />;
  if (status === 'read') return <CheckCheck className="h-3.5 w-3.5 text-[#075985]" />;
  return null;
}

function MessageBubble({ m, onReply }) {
  const isOut = m.direction === 'out';
  const isMedia = ['image', 'video', 'audio', 'document', 'sticker'].includes(m.msg_type);
  const mediaUrl = m.wa_message_id ? `${API_BASE}/inbox/media/by-message/${m.wa_message_id}` : null;

  return (
    <div className={`flex w-full ${isOut ? 'justify-end' : 'justify-start'} group`}>
      <div className={`max-w-[80%] sm:max-w-[68%] rounded-2xl px-3 py-2 shadow-sm relative
        ${isOut ? 'bg-[#FFE2D1]/85 text-[hsl(var(--foreground))] border border-[#FDBA74]/40' : 'bg-white/85 text-[hsl(var(--foreground))] border border-white/70'}`}>
        {isMedia && m.msg_type === 'image' && mediaUrl && (
          <img src={mediaUrl} alt="" className="rounded-xl max-h-[260px] mb-1 cursor-pointer" onClick={() => window.open(mediaUrl, '_blank')} loading="lazy" />
        )}
        {isMedia && m.msg_type === 'video' && mediaUrl && (
          <video src={mediaUrl} controls className="rounded-xl max-h-[260px] mb-1" />
        )}
        {isMedia && m.msg_type === 'audio' && mediaUrl && (
          <audio src={mediaUrl} controls className="w-[240px] mb-1" />
        )}
        {isMedia && m.msg_type === 'document' && mediaUrl && (
          <a href={mediaUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 mb-1 p-2 rounded-lg bg-white/40 hover:bg-white/70">
            <FileIcon className="h-5 w-5 text-[#9A3412]" />
            <span className="text-sm truncate">{m.media_filename || 'document.pdf'}</span>
          </a>
        )}
        {isMedia && m.msg_type === 'sticker' && mediaUrl && (
          <img src={mediaUrl} alt="" className="h-32 mb-1" />
        )}
        {m.msg_type === 'location' && (
          <div className="flex items-center gap-2 mb-1 p-2 rounded-lg bg-white/40">
            <MapPin className="h-4 w-4 text-[#9A3412]" />
            <span className="text-sm">Location shared</span>
          </div>
        )}
        {m.body && <div className="text-sm whitespace-pre-wrap break-words leading-snug">{m.body}</div>}
        <div className={`flex items-center gap-1 justify-end text-[10px] mt-0.5 ${isOut ? 'text-[#9A3412]/70' : 'text-muted-foreground'}`}>
          <span>{fmtTime(m.timestamp || m.created_at)}</span>
          {isOut && <StatusTicks status={m.status} />}
        </div>
        <button
          onClick={() => onReply?.(m)}
          className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-white/80 rounded-full p-1 shadow"
          title="Reply"
          data-testid={`inbox-reply-${m.id}`}
        >
          <Reply className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}

function ConversationItem({ conv, active, onClick }) {
  const name = conv?.contacts?.name || conv?.profile_name || conv?.phone || 'Unknown';
  const initials = (name || '?').slice(0, 1).toUpperCase();
  const dir = conv?.last_message_direction;
  const preview = conv?.last_message_text || '';
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${active ? 'bg-[#FFE2D1]/80 border border-white/60' : 'hover:bg-white/55'}`}
      data-testid={`inbox-conversation-${conv.id}`}
    >
      <div className="h-11 w-11 rounded-full gradient-orange text-white grid place-items-center font-semibold shrink-0">{initials}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium truncate text-sm">{name}</div>
          <div className="text-[10px] text-muted-foreground shrink-0">{fmtTime(conv.last_message_at)}</div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground truncate flex-1">
            {dir === 'out' && <CheckCheck className="inline h-3 w-3 mr-1 text-muted-foreground/70" />}
            {preview || <em>No messages yet</em>}
          </div>
          {(conv.unread_count || 0) > 0 && (
            <span className="text-[10px] font-semibold bg-[#F25C2A] text-white rounded-full px-1.5 min-w-[18px] h-[18px] grid place-items-center">{conv.unread_count}</span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function InboxPage() {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [search, setSearch] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recTimerRef = useRef(null);
  const prevActiveIdRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const r = await api.get('/inbox/conversations');
      setConversations(r.data || []);
    } catch {
      // silent
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  const loadMessages = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const r = await api.get(`/inbox/conversations/${cid}/messages`);
      setMessages((prev) => {
        const next = r.data || [];
        // Avoid unnecessary re-render if same content
        if (prev.length === next.length && prev.every((m, i) => m.id === next[i]?.id && m.status === next[i]?.status)) return prev;
        return next;
      });
    } catch {
      // silent
    }
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  // Auto-select first conversation
  useEffect(() => {
    if (!activeId && conversations.length) {
      setActiveId(conversations[0].id);
    }
  }, [conversations, activeId]);

  // Load active conv + messages when activeId changes
  useEffect(() => {
    if (!activeId) return;
    const c = conversations.find((x) => x.id === activeId);
    setActiveConv(c || null);
    // Only show loading + clear messages when switching to a different conversation
    if (prevActiveIdRef.current !== activeId) {
      prevActiveIdRef.current = activeId;
      setMessages([]);
      setLoadingMsgs(true);
      loadMessages(activeId).finally(() => setLoadingMsgs(false));
      // mark as read on open
      api.post(`/inbox/conversations/${activeId}/read`).catch(() => {});
    }
  }, [activeId, conversations, loadMessages]);

  // Polling
  useEffect(() => {
    const interval = setInterval(() => {
      loadConvs();
      if (activeId) loadMessages(activeId);
    }, 3500);
    return () => clearInterval(interval);
  }, [activeId, loadConvs, loadMessages]);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, activeId]);

  const filteredConvs = useMemo(() => {
    if (!search) return conversations;
    const s = search.toLowerCase();
    return conversations.filter((c) => (
      (c.contacts?.name || '').toLowerCase().includes(s) ||
      (c.profile_name || '').toLowerCase().includes(s) ||
      (c.phone || '').toLowerCase().includes(s)
    ));
  }, [conversations, search]);

  const sendText = async () => {
    const body = text.trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      await api.post(`/inbox/conversations/${activeId}/send-text`, { body, reply_to_wamid: replyTo?.wa_message_id || null });
      setText('');
      setReplyTo(null);
      await loadMessages(activeId);
      await loadConvs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const onPickFile = () => fileRef.current?.click();

  const sendFile = async (file, caption = '') => {
    if (!file || !activeId) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (caption) fd.append('caption', caption);
      await api.post(`/inbox/conversations/${activeId}/send-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadMessages(activeId);
      await loadConvs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setSending(false);
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/ogg' });
        const file = new File([blob], `voice_${Date.now()}.ogg`, { type: 'audio/ogg' });
        stream.getTracks().forEach((t) => t.stop());
        await sendFile(file);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecordingTime(0);
      recTimerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = (cancel = false) => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    if (cancel) audioChunksRef.current = [];
    try { mr.stop(); } catch {}
    setRecording(false);
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
  };

  const within24h = !!activeConv?.is_within_24h;
  const activeName = activeConv?.contacts?.name || activeConv?.profile_name || activeConv?.phone || '';
  const activeInitials = (activeName || '?').slice(0, 1).toUpperCase();

  return (
    <div className="h-[calc(100vh-7rem)] lg:h-[calc(100vh-6rem)] -mx-4 sm:-mx-6 lg:-mx-8">
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] h-full">
        {/* Left: Conversations */}
        <aside className={`${showChatOnMobile ? 'hidden' : 'flex'} lg:flex flex-col border-r border-white/40 bg-white/35 backdrop-blur-md h-full`}>
          <div className="p-3 border-b border-white/40 flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search chats..." className="bg-white/70 border-white/60 rounded-xl h-9" data-testid="inbox-search-input" />
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin p-2 space-y-1">
            {loadingConvs ? (
              <div className="py-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
            ) : filteredConvs.length === 0 ? (
              <div className="py-10 text-center">
                <div className="inline-flex h-12 w-12 rounded-2xl bg-[#FFE2D1] text-[#9A3412] items-center justify-center mb-2"><MessageSquare className="h-6 w-6" /></div>
                <div className="text-sm text-muted-foreground px-3">No conversations yet. Customers ko aap par WhatsApp pe message bhejne ko bolo — yaha real-time aayega.</div>
              </div>
            ) : filteredConvs.map((c) => (
              <ConversationItem
                key={c.id}
                conv={c}
                active={c.id === activeId}
                onClick={() => { setActiveId(c.id); setShowChatOnMobile(true); }}
              />
            ))}
          </div>
        </aside>

        {/* Right: Chat thread */}
        <section className={`${showChatOnMobile ? 'flex' : 'hidden'} lg:flex flex-col h-full bg-gradient-to-b from-white/30 to-white/15`}>
          {!activeConv ? (
            <div className="flex-1 grid place-items-center text-center px-6">
              <div>
                <div className="inline-flex h-16 w-16 rounded-2xl bg-[#FFE2D1] text-[#9A3412] items-center justify-center mb-3"><MessageSquare className="h-8 w-8" /></div>
                <div className="text-lg font-semibold">Welcome to Inbox</div>
                <div className="text-sm text-muted-foreground mt-1">Select a conversation to start chatting.</div>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-3 sm:px-5 py-3 border-b border-white/40 bg-white/40 backdrop-blur-md flex items-center gap-3" data-testid="inbox-chat-header">
                <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setShowChatOnMobile(false)} aria-label="Back" data-testid="inbox-mobile-back">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="h-10 w-10 rounded-full gradient-orange text-white grid place-items-center font-semibold">{activeInitials}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{activeName}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <Phone className="h-3 w-3" /> {activeConv.phone}
                    {within24h ? (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#EAF8F1] text-[#0F6B45] px-1.5 py-0.5 rounded-full border border-[#86EFAC]">24h open</span>
                    ) : (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#FEECEE] text-[#9F1239] px-1.5 py-0.5 rounded-full border border-[#FDA4AF]">24h closed</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto scrollbar-thin px-3 sm:px-6 py-4 space-y-2.5" data-testid="inbox-messages">
                {loadingMsgs ? (
                  <div className="py-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
                ) : messages.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">No messages in this conversation yet.</div>
                ) : messages.map((m) => (
                  <MessageBubble key={m.id} m={m} onReply={setReplyTo} />
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Composer */}
              <div className="p-3 sm:p-4 border-t border-white/40 bg-white/55 backdrop-blur-md">
                {replyTo && (
                  <div className="mb-2 glass p-2 rounded-xl flex items-start gap-2">
                    <Reply className="h-4 w-4 text-[#9A3412] mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Replying to</div>
                      <div className="text-xs truncate">{replyTo.body || `[${replyTo.msg_type}]`}</div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setReplyTo(null)} aria-label="Cancel reply" className="h-7 w-7">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {!within24h && (
                  <div className="mb-2 text-xs text-[#9F1239] bg-[#FEECEE] border border-[#FDA4AF] px-3 py-2 rounded-xl">
                    24-hour customer service window closed. You can only send approved template messages — use <a href="/campaigns" className="underline font-medium">Campaigns</a>.
                  </div>
                )}
                {recording ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 glass rounded-xl px-3 py-2 flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-[#F25C2A] animate-pulse" />
                      <span className="text-sm tabular-nums">Recording... {String(Math.floor(recordingTime / 60)).padStart(2, '0')}:{String(recordingTime % 60).padStart(2, '0')}</span>
                    </div>
                    <Button variant="outline" onClick={() => stopRecording(true)} className="rounded-xl bg-white/60 border-white/60" data-testid="inbox-cancel-recording">
                      <X className="h-4 w-4 mr-1.5" />Cancel
                    </Button>
                    <Button onClick={() => stopRecording(false)} className="gradient-orange text-white rounded-xl" data-testid="inbox-stop-recording">
                      <Send className="h-4 w-4 mr-1.5" />Send
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-end gap-2">
                    <Button variant="ghost" size="icon" onClick={onPickFile} disabled={!within24h || sending} aria-label="Attach" data-testid="inbox-attach-button"><Paperclip className="h-5 w-5" /></Button>
                    <input ref={fileRef} type="file" className="hidden" onChange={(e) => sendFile(e.target.files?.[0])} />
                    <Textarea
                      rows={1}
                      placeholder={within24h ? 'Type a message...' : 'Open the 24h window with a template first'}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); }
                      }}
                      disabled={!within24h || sending}
                      className="bg-white/80 border-white/60 rounded-xl resize-none min-h-[44px] max-h-32"
                      data-testid="inbox-message-input"
                    />
                    {text.trim().length === 0 ? (
                      <Button variant="ghost" size="icon" onClick={startRecording} disabled={!within24h || sending} aria-label="Record voice" data-testid="inbox-record-button"><Mic className="h-5 w-5" /></Button>
                    ) : (
                      <Button onClick={sendText} disabled={sending} className="gradient-orange text-white rounded-xl h-11 w-11 p-0" aria-label="Send" data-testid="inbox-send-button">
                        {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
